import React, { useEffect, useState } from 'react'
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Image,
} from '@react-pdf/renderer'
import Logo from '../../../assets/images/darkLogo.png'
import dayjs from 'dayjs'

const styles = StyleSheet.create({
    page: {
        padding: 24,
        fontSize: 9,
        fontFamily: 'Helvetica',
        backgroundColor: '#fff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: 16,
        paddingBottom: 8,
    },
    logo: {
        height: 18,
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#FFF',
    },
    subTitle: {
        fontSize: 10,
        marginBottom: 4,
        color: '#444',
    },
    tableHeader: {
        flexDirection: 'row',
    },
    tableRow: {
        flexDirection: 'row',
    },
    tableCell: {
        flex: 1,
        paddingHorizontal: 4,
        paddingVertical: 10,
        borderWidth: 0.5,
        borderColor: '#999999',
    },
    tableCellFlex5: {
        flex: 5,
        paddingHorizontal: 4,
        paddingVertical: 10,
        borderWidth: 0.5,
        borderColor: '#999999',
    },
    bold: {
        fontWeight: 'bold',
    },
    smallText: {
        fontSize: 8,
        color: '#666',
    },
    tableMainDiv: {
        marginTop: 12,
    },
    totalSummaryRow: {
        flexDirection: 'row',
        backgroundColor: '#e6f7ff',
        borderTopWidth: 1,
        borderColor: '#91d5ff',
    },
    totalSummaryCell: {
        flex: 1,
        padding: 4,
        borderWidth: 0.5,
        borderColor: '#91d5ff',
        fontWeight: 'bold',
    },
    totalSummaryCellFlex5: {
        flex: 5,
        padding: 4,
        borderWidth: 0.5,
        borderColor: '#91d5ff',
        fontWeight: 'bold',
    },
})

const hourMinuteSecond = (ms) => {
    const h = Math.floor(ms / 3600000)
    const m = Math.floor((ms % 3600000) / 60000)
    return `${h}h ${m}m`
}

const getTotalUserTime = (user) =>
    user?.list?.reduce((acc, item) => acc + (item?.totalLog?.total || 0), 0)

const ReportDocument = ({ pdfData, selectPdfData }) => {
    const [totalTime, setTotalTime] = useState(0)

    useEffect(() => {
        if (pdfData?.tableBody?.length > 0) {
            const total = pdfData.tableBody.reduce(
                (sum, user) => sum + getTotalUserTime(user),
                0
            )
            setTotalTime(total)
        }
    }, [pdfData?.tableBody])

    const tocList = pdfData?.tableBody?.map((user) => {
        const assignee = selectPdfData?.assignees?.find(
            (a) => a?.value === user?._id?.assigneeId
        )
        return {
            name: assignee?.label || 'Unknown',
            total: hourMinuteSecond(getTotalUserTime(user)),
            page: '-',
        }
    })

    return (
        <Document>
            {/* Page 1: TOC */}
            <Page size="A4" style={styles.page}>
                <View style={styles.header}>
                    <Image src={Logo} style={styles.logo} />
                </View>

                <View style={styles.tableMainDiv}>
                    {selectPdfData?.project?.name && (
                        <Text style={{ fontWeight: '700', fontSize: '16px', marginBottom: "5px" }}>
                            {selectPdfData?.project?.name ? selectPdfData?.project?.name : "Reports"}
                        </Text>
                    )}
                    <Text style={[styles.subTitle, { marginBottom: "20px" }]}>
                        {dayjs(selectPdfData?.start).format('DD MMM YYYY')}{'  -  '}{dayjs(selectPdfData?.end).format('DD MMM YYYY')}
                    </Text>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCellFlex5, styles.bold]}>
                            Resource
                        </Text>
                        <Text
                            style={[
                                styles.tableCell,
                                styles.bold,
                                { textAlign: 'center' },
                            ]}
                        >
                            Hours
                        </Text>
                    </View>
                    {tocList?.map((item, idx) => (
                        <View key={idx} style={styles.tableRow}>
                            <Text style={styles.tableCellFlex5}>
                                {item.name}
                            </Text>
                            <Text
                                style={[styles.tableCell, { textAlign: 'center' }]}
                            >
                                {item.total}
                            </Text>
                        </View>
                    ))}
                    <View style={styles.totalSummaryRow}>
                        <Text style={styles.totalSummaryCellFlex5}>Total Hours</Text>
                        <Text style={[styles.totalSummaryCell, { textAlign: 'center' }]}>
                            {hourMinuteSecond(totalTime)}
                        </Text>
                    </View>
                </View>
            </Page>

            {/* Log Pages */}
            {pdfData?.tableBody?.map((user, userIdx) => {
                const assignee = selectPdfData?.assignees?.find(
                    (a) => a?.value === user?._id?.assigneeId
                )
                if (!assignee) return null

                const rows = user?.list?.flatMap((task) =>
                    task?.list?.map((l) => ({
                        title: l?.title,
                        description: l?.description,
                        start: l?.timelogs?.start,
                        end: l?.timelogs?.end,
                    }))
                )

                return (
                    <Page size="A4" style={styles.page} key={userIdx}>
                        <View style={{
                            backgroundColor: "#285FD0",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            marginBottom: 6,
                            padding: 8,
                            borderRadius: 2
                        }}>
                            <Text style={styles.sectionTitle}>
                                {assignee?.label}
                            </Text>
                            <Text style={styles.sectionTitle}>
                                {hourMinuteSecond(getTotalUserTime(user))}
                            </Text>
                        </View>

                        <View style={styles.tableHeader}>
                            <Text style={[styles.tableCellFlex5, styles.bold]}>
                                Task
                            </Text>
                            <Text style={[styles.tableCell, styles.bold]}>
                                Date
                            </Text>
                            <Text style={[styles.tableCell, styles.bold]}>
                                Duration
                            </Text>
                        </View>

                        {rows?.map((row, idx) => (
                            <View key={idx} style={styles.tableRow} wrap={false}>
                                <Text style={styles.tableCellFlex5}>
                                    <View style={{ maxWidth: "250px", flexDirection: "row" }}>
                                        <Text>{row.title}</Text>
                                        <Text style={styles.smallText}> {row.description}</Text>
                                    </View>
                                </Text>
                                <Text style={styles.tableCell}>
                                    {dayjs(row.start).format('DD MMM YYYY')}
                                </Text>
                                <Text style={styles.tableCell}>
                                    {row.end
                                        ? hourMinuteSecond(row.end - row.start)
                                        : hourMinuteSecond(Date.now() - row.start)}
                                </Text>
                            </View>
                        ))}
                    </Page>
                )
            })}
        </Document>
    )
}

export default ReportDocument
